import {
  doc,
  setDoc,
  addDoc,
  updateDoc,
  collection,
  serverTimestamp,
  waitForPendingWrites,
} from "firebase/firestore";
import { getDb, FIRESTORE_PATH } from "./firebase";
import { getMelderString } from "./operator";
import type { IncidentEntry } from "./types";

// Module-level state for the pending-writes indicator. Components subscribe
// via subscribePending(); each write is wrapped with trackPending() to
// surface "syncing" state from issue-time until the SDK confirms server flush.
let pendingCount = 0;
const listeners = new Set<(p: boolean) => void>();

function emit() {
  for (const l of listeners) l(pendingCount > 0);
}

export function subscribePending(cb: (p: boolean) => void): () => void {
  listeners.add(cb);
  cb(pendingCount > 0);
  return () => listeners.delete(cb);
}

// Track a Firestore write so that the global pending-writes counter stays
// > 0 from issue-time until the SDK confirms server flush. The promise must
// already represent a queued write — calling waitForPendingWrites *before*
// setDoc/addDoc/updateDoc registers with the SDK resolves instantly because
// the SDK has nothing pending yet. Always pass the write promise.
function trackPending<T>(db: import("firebase/firestore").Firestore, writePromise: Promise<T>): Promise<T> {
  pendingCount++;
  emit();
  // Wait until the write has been registered (microtask), then ask the SDK
  // when its outstanding writes are flushed. Offline → resolves on reconnect.
  void writePromise
    .catch(() => undefined)
    .then(() => waitForPendingWrites(db))
    .finally(() => {
      pendingCount = Math.max(0, pendingCount - 1);
      emit();
    });
  return writePromise;
}

export async function setChecklistItem(
  id: string,
  checked: boolean,
  by?: string,
): Promise<void> {
  const db = getDb();
  if (!db) {
    // Only fires when Firebase env vars are missing entirely (config-less
    // local dev). In production the SDK's IndexedDB queue handles offline.
    cacheLocal(`checklist/${id}`, { id, checked, by, at: Date.now() });
    return;
  }
  await trackPending(
    db,
    setDoc(
      doc(db, `${FIRESTORE_PATH}/checklist/${id}`),
      {
        checked,
        by: by ?? null,
        at: serverTimestamp(),
      },
      { merge: true },
    ),
  );
}

export async function addIncident(
  incident: Omit<IncidentEntry, "id">,
): Promise<void> {
  const db = getDb();
  const melder = getMelderString();
  if (!db) {
    // Only fires when Firebase env vars are missing (config-less local dev).
    // Production offline path is the SDK's IndexedDB write queue.
    const id = `local-${Date.now()}`;
    cacheLocal(`incidents/${id}`, { ...incident, melder, id });
    return;
  }
  await trackPending(
    db,
    addDoc(collection(db, `${FIRESTORE_PATH}/incidents`), {
      ...incident,
      ts: serverTimestamp(),
      melder,
    }),
  );
}

export async function resolveIncident(
  id: string,
  resolved: boolean,
): Promise<void> {
  const db = getDb();
  if (!db) return;
  await trackPending(
    db,
    updateDoc(doc(db, `${FIRESTORE_PATH}/incidents/${id}`), { resolved }),
  );
}

export interface LeadRequestInput {
  titel: string;
  wanneer: string;
  toelichting: string;
  afzender: string;
}

export async function addLeadRequest(
  payload: LeadRequestInput,
): Promise<{ ok: boolean; offline: boolean }> {
  const db = getDb();
  const record = {
    ...payload,
    source: "nexxt-cp-launcher" as const,
    at: serverTimestamp(),
  };
  if (!db) {
    cacheLocal(`lead-${Date.now()}`, {
      ...payload,
      source: "nexxt-cp-launcher",
    });
    return { ok: true, offline: true };
  }
  await trackPending(
    db,
    addDoc(collection(db, `${FIRESTORE_PATH}/lead_requests`), record),
  );
  return { ok: true, offline: false };
}

function cacheLocal(path: string, value: unknown): void {
  try {
    localStorage.setItem(`pm26.local.${path}`, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

// Re-export so EccTimeline / Inzet can import getMelderString from this module.
export { getMelderString };
